import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Phone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Message = { role: "user" | "assistant"; content: string };

interface CollectedInfo {
  name?: string;
  email?: string;
  phone?: string;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/hvac-chat`;
const SCHEDULE_URL = "https://form.fillout.com/t/aUQUhSyfAXus";

const quickQuestions = [
  "What services do you offer?",
  "What areas in MN do you serve?",
  "How do I get a free quote?",
  "Do you offer snow removal?",
];

// Patterns to detect contact info
const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
const phonePattern = /(?:\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/;

// URL to friendly name mapping
const urlLabels: Record<string, string> = {
  "https://form.fillout.com/t/aUQUhSyfAXus": "Schedule a Quote",
  "https://lawnmammoth.lovable.app/embed/39673b4c-6825-43f1-9697-ad4e1647fba3": "Instant Estimate",
  "/services/lawn-care": "Lawn Care",
  "/services/fertilization": "Fertilization",
  "/services/aeration": "Aeration",
  "/services/mulching": "Mulching",
  "/services/hardscaping": "Hardscaping",
  "/services/snow-removal": "Snow Removal",
  "/service-areas": "Service Areas",
  "/about": "About Us",
  "/projects": "Our Projects",
};

// Helper to split text into max 2 chunks
const splitIntoChunks = (text: string): string[] => {
  // Split roughly in half by finding a good break point
  if (text.length <= 150) {
    return [text];
  }
  
  // Find a sentence break near the middle
  const midPoint = Math.floor(text.length / 2);
  const sentenceEnders = /[.!?]\s+/g;
  let bestBreak = -1;
  let match;
  
  while ((match = sentenceEnders.exec(text)) !== null) {
    if (match.index + match[0].length <= midPoint + 50) {
      bestBreak = match.index + match[0].length;
    }
  }
  
  if (bestBreak > 30 && bestBreak < text.length - 30) {
    return [text.slice(0, bestBreak).trim(), text.slice(bestBreak).trim()];
  }
  
  // If no good sentence break, just return as one chunk
  return [text];
};

// Delay helper
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hi! 👋 I can help answer questions about our lawn care, landscaping, and snow removal services. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [collectedInfo, setCollectedInfo] = useState<CollectedInfo>({});
  const [leadSaved, setLeadSaved] = useState(false);
  const [webhookSent, setWebhookSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(false);
  const abandonmentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Show popup after 4 seconds if chat isn't open and popup wasn't dismissed
  useEffect(() => {
    if (popupDismissed || isOpen) return;
    
    const timer = setTimeout(() => {
      if (!isOpen && !popupDismissed) {
        setShowPopup(true);
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [isOpen, popupDismissed]);

  // Hide popup when chat opens
  useEffect(() => {
    if (isOpen) {
      setShowPopup(false);
    }
  }, [isOpen]);

  const handlePopupClick = () => {
    setShowPopup(false);
    setPopupDismissed(true);
    setIsOpen(true);
  };

  const dismissPopup = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPopup(false);
    setPopupDismissed(true);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Abandonment timer - send partial lead data after 1 minute of inactivity
  useEffect(() => {
    if (!isOpen) return;
    
    const checkAbandonment = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      
      // If 1 minute of inactivity and we have some info but haven't sent webhook
      if (timeSinceActivity >= 60000 && !webhookSent && (collectedInfo.email || collectedInfo.phone || collectedInfo.name)) {
        const conversationSummary = messages
          .slice(-6)
          .map((m) => `${m.role}: ${m.content}`)
          .join("\n");
        
        // Parse name into first/last if possible
        const nameParts = (collectedInfo.name || "").trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/make-webhook`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            firstName,
            lastName,
            email: collectedInfo.email || "",
            phone: collectedInfo.phone || "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
            source: "chatbot_abandoned",
            conversationSummary,
          }),
        }).then(() => {
          setWebhookSent(true);
        }).catch(console.error);
      }
    };
    
    // Check every 15 seconds
    abandonmentTimerRef.current = setInterval(checkAbandonment, 15000);
    
    return () => {
      if (abandonmentTimerRef.current) {
        clearInterval(abandonmentTimerRef.current);
      }
    };
  }, [isOpen, webhookSent, collectedInfo, messages]);

  // Update last activity on user interaction
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Extract contact info from message
  const extractContactInfo = (text: string, currentInfo: CollectedInfo): CollectedInfo => {
    const newInfo = { ...currentInfo };
    
    const emailMatch = text.match(emailPattern);
    if (emailMatch) {
      newInfo.email = emailMatch[0];
    }
    
    const phoneMatch = text.match(phonePattern);
    if (phoneMatch) {
      newInfo.phone = phoneMatch[0];
    }
    
    const namePatterns = [
      /(?:I'm|I am|My name is|This is|It's|Call me)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)$/,
    ];
    
    for (const pattern of namePatterns) {
      const nameMatch = text.match(pattern);
      if (nameMatch && nameMatch[1] && nameMatch[1].length > 1 && nameMatch[1].length < 50) {
        const notNames = ["yes", "no", "maybe", "thanks", "thank", "hello", "hi", "hey", "ok", "okay"];
        if (!notNames.includes(nameMatch[1].toLowerCase())) {
          newInfo.name = nameMatch[1];
        }
      }
    }
    
    return newInfo;
  };

  // Send lead to Make.com webhook
  const sendToWebhook = useCallback(async (info: CollectedInfo, conversationSummary: string) => {
    if (webhookSent || (!info.email && !info.phone)) return;
    
    try {
      // Parse name into first/last if possible
      const nameParts = (info.name || "").trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/make-webhook`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email: info.email || "",
          phone: info.phone || "",
          address: "",
          city: "",
          state: "",
          zipCode: "",
          source: "chatbot",
          conversationSummary,
        }),
      });
      
      setWebhookSent(true);
    } catch (error) {
      console.error("Error sending to webhook:", error);
    }
  }, [webhookSent]);

  // Save lead to database
  const saveLead = async (info: CollectedInfo, conversationSummary: string) => {
    if (leadSaved || (!info.email && !info.phone)) return;
    
    try {
      const response = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: [],
          saveLead: {
            name: info.name,
            email: info.email,
            phone: info.phone,
            conversationSummary,
          },
        }),
      });
      
      if (response.ok) {
        setLeadSaved(true);
        // Also send to Make.com webhook when lead is saved
        sendToWebhook(info, conversationSummary);
      }
    } catch (error) {
      console.error("Error saving lead:", error);
    }
  };

  const streamChat = async (userMessages: Message[]) => {
    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ messages: userMessages }),
    });

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to get response");
    }

    if (!resp.body) throw new Error("No response body");

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let fullResponse = "";

    // Collect the full response first
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") break;

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            fullResponse += content;
          }
        } catch {
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }

    // Now display the response in chunks with typing effect
    const chunks = splitIntoChunks(fullResponse);
    
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      
      // Show typing indicator
      setIsTyping(true);
      
      // Simulate typing delay (40-60ms per character, capped)
      const typingDelay = Math.min(chunk.length * 25, 1500);
      await delay(typingDelay);
      
      setIsTyping(false);
      
      // Add the chunk as a new message
      setMessages(prev => [...prev, { role: "assistant", content: chunk }]);
      
      // Small pause between messages
      if (i < chunks.length - 1) {
        await delay(400);
      }
    }
  };

  const handleSend = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    // Update activity timestamp for abandonment tracking
    updateActivity();

    const newInfo = extractContactInfo(text, collectedInfo);
    if (newInfo.name !== collectedInfo.name || newInfo.email !== collectedInfo.email || newInfo.phone !== collectedInfo.phone) {
      setCollectedInfo(newInfo);
      
      if ((newInfo.email || newInfo.phone) && !leadSaved) {
        const conversationSummary = messages
          .slice(-6)
          .map((m) => `${m.role}: ${m.content}`)
          .join("\n");
        saveLead(newInfo, conversationSummary);
      }
    }

    const userMessage: Message = { role: "user", content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      await streamChat(newMessages);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I'm having trouble connecting right now. Please call us at (763) 245-7813 for immediate assistance!",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Clean markdown formatting from text
  const cleanMarkdown = (text: string): string => {
    return text
      // Remove bold markers **text** or __text__
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      // Remove italic markers *text* or _text_
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      // Remove bullet points at start of lines
      .replace(/^\s*[\*\-•]\s+/gm, '• ')
      // Remove numbered list markers
      .replace(/^\s*\d+\.\s+/gm, '')
      // Clean up extra whitespace
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Render message with clickable links
  const renderMessage = (content: string) => {
    // First clean markdown
    const cleanedContent = cleanMarkdown(content);
    
    const urlPattern = /(https?:\/\/[^\s,)]+|\/services\/[^\s,)]+|\/service-areas|\/about|\/projects)/g;
    const parts = cleanedContent.split(urlPattern);
    
    return parts.map((part, idx) => {
      if (part.match(urlPattern)) {
        // Clean up any trailing punctuation from the URL
        const cleanUrl = part.replace(/[.,;:!?]+$/, '');
        const trailingPunctuation = part.slice(cleanUrl.length);
        
        // Get friendly label or create one from the URL
        let label = urlLabels[cleanUrl];
        if (!label) {
          // Create a readable label from the URL path
          const urlPath = cleanUrl.replace(/https?:\/\/[^/]+/, '');
          if (urlPath && urlPath !== '/') {
            label = urlPath
              .split('/')
              .filter(Boolean)
              .pop()
              ?.replace(/-/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase()) || 'View Page';
          } else {
            label = 'Visit Website';
          }
        }
        
        // Check if it's an internal link
        const isInternal = cleanUrl.startsWith('/');
        
        return (
          <span key={idx}>
            <a
              href={cleanUrl}
              target={isInternal ? undefined : "_blank"}
              rel={isInternal ? undefined : "noopener noreferrer"}
              className="text-secondary font-medium underline hover:text-secondary/80"
            >
              {label}
            </a>
            {trailingPunctuation}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50 flex flex-col items-end gap-3">
      {/* Chat Window */}
      <div
        className={cn(
          "absolute bottom-16 right-0 w-[calc(100vw-2rem)] sm:w-[360px] max-w-[360px] bg-card rounded-2xl shadow-2xl border border-border overflow-hidden transition-all duration-300",
          isOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        )}
      >
        {/* Header */}
        <div className="bg-primary p-4 text-primary-foreground">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-secondary-foreground" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Riekels</h3>
                <p className="text-xs opacity-90">Here to help!</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="h-80 overflow-y-auto p-4 space-y-3 bg-background">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={cn(
                "flex animate-in fade-in slide-in-from-bottom-2 duration-300",
                msg.role === "user" ? "justify-end" : "justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-2 text-sm",
                  msg.role === "user"
                    ? "bg-secondary text-secondary-foreground rounded-br-md"
                    : "bg-muted text-foreground rounded-bl-md"
                )}
              >
                {msg.role === "assistant" ? renderMessage(msg.content) : msg.content}
              </div>
            </div>
          ))}
          {(isLoading || isTyping) && (
            <div className="flex justify-start animate-in fade-in duration-200">
              <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Questions */}
        {messages.length <= 2 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {quickQuestions.map((q, idx) => (
              <button
                key={idx}
                onClick={() => handleSend(q)}
                className="text-xs bg-muted hover:bg-muted/80 text-foreground px-3 py-1.5 rounded-full transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-border bg-card">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              className="flex-1"
              disabled={isLoading}
            />
            <Button
              onClick={() => handleSend()}
              size="icon"
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              disabled={isLoading || !input.trim()}
              aria-label="Send message"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Phone className="w-3 h-3" />
            <span>Or call us:</span>
            <a
              href="tel:+17632457813"
              className="text-secondary font-semibold hover:underline"
            >
              (763) 245-7813
            </a>
          </div>
        </div>
      </div>

      {/* Popup Message */}
      {showPopup && !isOpen && (
        <div
          onClick={handlePopupClick}
          className="absolute bottom-16 right-0 mb-2 w-64 bg-card rounded-xl shadow-xl border border-border p-4 cursor-pointer animate-fade-in"
        >
          <button
            onClick={dismissPopup}
            className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss popup"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
          <p className="text-sm text-foreground pr-4">
            <span className="font-semibold">Hi! 👋</span> Need help with lawn care or landscaping? I'm here to answer your questions!
          </p>
          <div className="absolute bottom-0 right-6 translate-y-1/2 rotate-45 w-3 h-3 bg-card border-r border-b border-border" />
        </div>
      )}

      {/* Chat Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ring-2 ring-white ring-offset-2 ring-offset-transparent",
          isOpen
            ? "bg-muted text-foreground"
            : "bg-secondary text-secondary-foreground hover:scale-110"
        )}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <MessageCircle className="w-6 h-6" aria-hidden="true" />}
      </button>
    </div>
  );
};

